import { useQuery } from '@tanstack/react-query';
import { inspectionReportService } from '@/services/inspection-report.service';
import { REPORT_EVENT_LABELS } from '../../report/report-workflow';

interface Props {
  inspectionId: string;
}

function fmtDateTime(v: string): string {
  return new Date(v).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Workflow history in chronological order (newest first, as the API returns). */
export function ReportHistoryList({ inspectionId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['report-history', inspectionId],
    queryFn: () => inspectionReportService.getHistory(inspectionId, 1, 100),
    staleTime: 10_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="report-history-loading">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  const events = data?.data ?? [];
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">No workflow history yet.</p>;
  }

  return (
    <ol className="space-y-3" data-testid="report-history">
      {events.map((e) => (
        <li key={e.id} className="flex gap-3 text-sm">
          <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium text-foreground">
                {REPORT_EVENT_LABELS[e.eventType] ?? e.eventType}
              </span>
              {e.actorRoleName && (
                <span className="text-xs text-muted-foreground">{e.actorRoleName}</span>
              )}
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                {fmtDateTime(e.createdAt)}
              </span>
            </div>
            {e.reason && (
              <p className="mt-0.5 whitespace-pre-wrap text-sm text-muted-foreground">
                Reason: {e.reason}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
